import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEdit, faTrash, faUserCircle, faCaretDown, 
  faUser, faSyncAlt, faSignOutAlt, faCrown, faCheckCircle, 
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import '../scss/MachineManagement.scss';
import BaseURL from '../assets/contants/BaseURL';

const MachineManagement = () => {
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    return headers;
  };

  const authFetch = async (url, options = {}) => {
    const headers = getAuthHeaders();
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    };

    if (!getAuthToken()) {
      showNotification('Please login first', 'error');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(url, config);
    
    if (response.status === 401) {
      showNotification('Session expired. Please login again.', 'error');
      sessionStorage.removeItem('Token');
      sessionStorage.removeItem('session_id');
      localStorage.removeItem('Token');
      localStorage.removeItem('session_id');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      throw new Error('Unauthorized');
    }
    
    return response;
  };

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const fetchMachineData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${BaseURL}meters/cms-cranes/`);
      if (response.ok) {
        const data = await response.json();
        setMachines(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching machine data:', error);
      if (error.message !== 'No authentication token found' && error.message !== 'Unauthorized') {
        showNotification('Failed to load machine data', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteMachine = async (machineId) => {
    if (window.confirm('Are you sure you want to delete this machine?')) {
      try {
        const response = await authFetch(`${BaseURL}meters/cms-cranes/${machineId}/`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          showNotification('Machine deleted successfully', 'success');
          fetchMachineData();
        } else {
          throw new Error('Failed to delete machine');
        }
      } catch (error) {
        console.error('Error deleting machine:', error);
        if (error.message !== 'No authentication token found' && error.message !== 'Unauthorized') {
          showNotification('Failed to delete machine', 'error');
        }
      }
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      sessionStorage.removeItem('Token');
      sessionStorage.removeItem('session_id');
      sessionStorage.removeItem('username');
      localStorage.removeItem('Token');
      localStorage.removeItem('session_id');
      localStorage.removeItem('username');
      
      showNotification('Logged out successfully', 'success');
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    }
  };

  useEffect(() => {
    if (!getAuthToken()) {
      showNotification('Please login to access this page', 'error');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      return;
    }
    
    fetchMachineData();
  }, [fetchMachineData, navigate]);

  return (
    <div className="machine-management-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {orgLogo ? (
                                  <img
                                    src={orgLogo}
                                    alt="Organization Logo"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '2px solid #4361ee'
                                    }}
                                    onError={() => setOrgLogo(null)}
                                  />
                                ) : (
                                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {isDropdownOpen && (
                <div className="account-menu show">
                  <div className="account-menu-item plan-info">
                    <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                    <span>Basic Plan</span>
                  </div>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faUser} /> Profile
                  </button>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                  </button>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-titles">
          <h1>Machine Management</h1>
          <p>Manage all machine configurations and details</p>
        </div>

        {/* Action Buttons */}
        <div className="actionbuttons">
          <Link to="/machine/add" className="btn btn-primary">
            <FontAwesomeIcon icon={faPlus} /> Add Machine
          </Link>
        </div>

        {/* Machine Table */}
        <div className="table-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading machines...</p>
            </div>
          ) : (
            <table className="machine-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Machine ID</th>
                  <th>Device ID</th>
                  <th>Name</th>
                  <th>Manufacturer</th>
                  <th>Model</th>
                  <th>Line</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {machines.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
                      No machines found
                    </td>
                  </tr>
                ) : (
                  machines.map((machine, index) => (
                    <tr key={machine.id}>
                      <td>{index + 1}</td>
                      <td>{machine.machine_id || 'N/A'}</td>
                      <td>{machine.device_name || 'N/A'}</td>
                      <td>{machine.machine_name || 'N/A'}</td>
                      <td>{machine.manufacture || 'N/A'}</td>
                      <td>{machine.model || 'N/A'}</td>
                      <td>{machine.line || 'N/A'}</td>
                      <td className="action-btns">
                        <Link to={`/machine/edit/${machine.id}`} className="edit-btn">
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button className="delete-btn" onClick={() => deleteMachine(machine.id)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default MachineManagement;